import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { ChevronLeft, Plus, Search, MapPin, Phone, Trash2, User } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EnderecoService, type EnderecoSnapshotResponseDto } from "@/services";
import { TOKEN_STORAGE_KEY, USER_STORAGE_KEY } from "@/config/base";
import { formatPhoneInput } from "@/lib/utils";
import { SavedAddressesSkeleton } from "@/components/profile/profile-skeletons";
import { AddAddressForm } from "@/components/profile/add-address-form";
import { showToast } from "@/hooks/useToast";

export const Route = createFileRoute("/app/perfil_/enderecos")({
  beforeLoad: () => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const user = localStorage.getItem(USER_STORAGE_KEY);

    if (!token || !user) {
      throw redirect({ to: "/auth/identify" });
    }
  },
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [enderecos, setEnderecos] = useState<EnderecoSnapshotResponseDto[]>([]);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const currentUser = useMemo(() => {
    try {
      const raw = localStorage.getItem(USER_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const loadAddresses = async () => {
    setIsLoading(true);
    setError(false);
    try {
      const data = await EnderecoService.findAll();
      setEnderecos(data);
    } catch {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAddresses();
  }, []);

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    try {
      await EnderecoService.remove(id);
      setEnderecos((prev) => prev.filter((e) => e.id !== id));
      showToast.success("Endereço removido com sucesso!");
    } catch {
      showToast.error("Erro ao remover endereço. Tente novamente.");
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  const isCurrentUser = (item: EnderecoSnapshotResponseDto) => {
    if (!currentUser) return false;
    return (
      item.nome === currentUser.nome &&
      item.telefone.replace(/\D/g, "") === String(currentUser.telefone ?? "").replace(/\D/g, "")
    );
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return enderecos;
    const q = search.toLowerCase().trim();
    return enderecos.filter(
      (e) =>
        e.nome.toLowerCase().includes(q) ||
        e.telefone.includes(q) ||
        e.logradouro.toLowerCase().includes(q) ||
        e.cidade.toLowerCase().includes(q) ||
        e.bairro.toLowerCase().includes(q)
    );
  }, [enderecos, search]);

  if (isAdding) {
    return (
      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-black/5">
          <div className="container mx-auto px-4 py-3 max-w-2xl">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsAdding(false)}
                className="rounded-full hover:bg-black/5 -ml-2"
              >
                <ChevronLeft className="h-5 w-5 text-primary" />
              </Button>
              <h1 className="text-base font-semibold text-slate-900">
                Adicionar Endereço
              </h1>
            </div>
          </div>
        </header>
        <main className="container mx-auto max-w-2xl px-4 py-4 pb-24">
          <AddAddressForm
            onCancel={() => setIsAdding(false)}
            onSuccess={() => {
              setIsAdding(false);
              loadAddresses();
            }}
          />
        </main>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${enderecos.length === 0 && !isLoading ? "bg-slate-50" : "bg-background"}`}>
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-black/5">
        <div className="container mx-auto px-4 py-3 max-w-2xl">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate({ to: "/app/perfil" })}
              className="rounded-full hover:bg-black/5 -ml-2"
            >
              <ChevronLeft className="h-5 w-5 text-primary" />
            </Button>

            <h1 className="text-base font-semibold text-slate-900">
              Endereços Salvos
            </h1>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsAdding(true)}
              className="rounded-full hover:bg-black/5 -mr-2"
            >
              <Plus className="h-5 w-5 text-primary" />
            </Button>
          </div>
        </div>
      </header>

      <div className="sticky top-[57px] z-40 bg-white/80 backdrop-blur-xl border-b border-black/5">
        <div className="container mx-auto px-4 py-2 max-w-2xl">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <Input
              placeholder="Buscar por nome, telefone ou endereço..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-slate-50 border-black/10 rounded-xl h-9 text-sm"
            />
          </div>
        </div>
      </div>

      <main className="container mx-auto max-w-2xl px-4 py-3 pb-24">
        {isLoading ? (
          <SavedAddressesSkeleton />
        ) : error ? (
          <div className="text-center py-12">
            <div className="bg-rose-50 text-rose-700 rounded-2xl p-5 inline-block border border-rose-100">
              <p className="font-medium text-sm">Erro ao carregar endereços</p>
              <Button
                variant="outline"
                size="sm"
                onClick={loadAddresses}
                className="mt-3 rounded-xl border-rose-200 hover:bg-rose-100"
              >
                Tentar Novamente
              </Button>
            </div>
          </div>
        ) : enderecos.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
            <div className="bg-slate-100 rounded-full p-4 mb-4">
              <MapPin className="h-8 w-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-700 mb-2">
              Nenhum endereço salvo
            </h3>
            <p className="text-sm text-slate-500 max-w-xs mb-6">
              Adicione endereços para agilizar suas próximas entregas.
            </p>
            <Button onClick={() => setIsAdding(true)} className="rounded-xl">
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Endereço
            </Button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[40vh] text-center px-4">
            <div className="bg-slate-100 rounded-full p-4 mb-4">
              <Search className="h-8 w-8 text-slate-400" />
            </div>
            <h3 className="text-base font-semibold text-slate-700 mb-1">
              Nenhum resultado
            </h3>
            <p className="text-sm text-slate-500">
              Tente buscar por outro nome ou endereço.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((item) => {
              const isSelf = isCurrentUser(item);
              return (
                <div
                  key={item.id}
                  className="bg-white rounded-2xl p-3 border border-black/5 flex gap-3 items-start transition-all"
                >
                  <div
                    className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                      isSelf ? "bg-primary/10" : "bg-slate-100"
                    }`}
                  >
                    <User
                      className={`w-4 h-4 ${isSelf ? "text-primary" : "text-slate-500"}`}
                    />
                  </div>

                  <div className="flex-1 min-w-0 space-y-0.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-slate-900 truncate">
                        {item.nome}
                      </p>
                      {isSelf && (
                        <span className="shrink-0 text-[10px] font-medium bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                          Seu endereço
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-slate-500">
                      <Phone className="w-3 h-3 text-slate-400 shrink-0" />
                      <span>{formatPhoneInput(item.telefone)}</span>
                    </div>
                    <div className="flex items-start gap-1.5 text-xs text-slate-500">
                      <MapPin className="w-3 h-3 text-slate-400 mt-0.5 shrink-0" />
                      <span className="line-clamp-1">
                        {item.logradouro}
                        {item.numero && `, ${item.numero}`}
                        {item.bairro && ` - ${item.bairro}`}
                        {item.cidade && `, ${item.cidade}`}
                      </span>
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0 h-8 w-8 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50"
                    onClick={() => setConfirmDeleteId(item.id)}
                    disabled={deletingId === item.id}
                  >
                    {deletingId === item.id ? (
                      <div className="h-3.5 w-3.5 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {confirmDeleteId !== null && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center">
          <div className="bg-white w-full max-w-lg rounded-t-3xl p-6 animate-in slide-in-from-bottom">
            <h3 className="text-lg font-semibold text-slate-900 text-center mb-2">
              Remover endereço?
            </h3>
            <p className="text-sm text-slate-500 text-center mb-6">
              Esta ação não pode ser desfeita.
            </p>
            <div className="space-y-3">
              <Button
                variant="destructive"
                className="w-full h-12 rounded-xl"
                onClick={() => handleDelete(confirmDeleteId)}
                disabled={deletingId !== null}
              >
                {deletingId !== null ? (
                  <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Remover
              </Button>
              <Button
                variant="outline"
                className="w-full h-12 rounded-xl"
                onClick={() => setConfirmDeleteId(null)}
                disabled={deletingId !== null}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
